import { forwardRef, Inject, Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { EventApi, EventDef } from '@fullcalendar/angular';
import {
  BehaviorSubject,
  of,
  Observable,
  throwError,
  combineLatest,
  forkJoin,
} from 'rxjs';
import { switchMap, catchError, take, tap, map } from 'rxjs/operators';
import { EventRef, EventRefBackend } from './calendar.model';
import { SzabadsagTipusokService } from '../../services/szabadsag-tipusok.service';
import { findByProp, parseJSON } from '../../utils';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { OnelszamoloEgysegekService } from '../../services/onelszamolo-egysegek.service';
import { MunkatarsakService } from '../../services/munkatarsak.service';

@Injectable({
  providedIn: 'root',
})
export class CalendarReworkService {
  private readonly apiEndpoint = 'szabadsagnaptar';
  private readonly szabadsagOKEndpoint = 'service/szabadsaglib/szabadsag_ok';
  private readonly szabadsagNaptarUpdateEndpoint = 'service/frissiteslib/szabadsag_frissites';
  private readonly dolgozoSzabiEndpoint = 'service/szabadsaglib/szabadsag_dolgozo_idra';
  private readonly szabadsagByTelephelyEndpoint = 'service/szabadsaglib/szabadsag_telephely'

  private readonly _data = new BehaviorSubject<EventRef[]>(null);
  private readonly _selectedData = new BehaviorSubject<EventRef | {}>({});
  private readonly _activeKategoriak = new BehaviorSubject<number[]>([
    1, 2, 3, 4, 5,
  ]);

  private lastFetch = new Date();

  private initialValue: any;

  private state$ = this._data.pipe(
    switchMap((state: EventRef[]) => (state ? of(state) : this.fetchData()))
  );

  public data$ = combineLatest([
    this.state$,
    this.szabiTipusokService.data$,
    this.oekService.data$
  ]).pipe(
    map(([events, szabiTipusok, oek]) => {
      return this.fromJSON(events, szabiTipusok, oek)
    }
      // events.filter(
      //   (e: EventRef) =>
      //     kategoriak.find((k: any) => k.id === e.szabadsagtipus_id)?.checked
      // )
    )
  );

  constructor(
    private api: ApiService,
    private stateService: StateService,
    private szabiTipusokService: SzabadsagTipusokService,
    private oekService: MunkatarsakService,
    private dbService: NgxIndexedDBService
  ) {}

  get initData$(): Observable<any> {
    return this._data.pipe(
      take(1),
      switchMap((state: EventRef[]) => (state ?
        forkJoin([
          of(state), 
          this.szabiTipusokService.initData$,
          this.oekService.initData$
        ]).pipe(
          map(([events, szabiTipusok, oek]) =>
            this.fromJSON(events, szabiTipusok, oek)
          )
        ) :
          this.fetchData()))
    );
  }

  get selectedData$(): Observable<EventRef> {
    return this._selectedData.pipe(map((todo: EventRef) => todo || null));
  }

  add(data: any): Observable<any> {
    return this.api.post(this.apiEndpoint, this.toJSON(data)).pipe(
      map((added: EventRefBackend) => added),
      tap((added: EventRef) => {
        this.stateService.setStateOnAdd(added, this._data);
      }),
      catchError((err: any) => throwError(err))
    );
  }

  addWithoutMsg(data: any): Observable<any> {
    return this.api.postWithoutMsg(this.apiEndpoint, this.toJSON(data)).pipe(
      map((added: EventRefBackend) => added),
      tap((added: EventRef) => {
        this.stateService.setStateOnAdd(added, this._data);
      }),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<EventRef> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((deleted: EventRef) => {
        this.stateService.setStateOnDelete(deleted, this._data);
        this._selectedData.next({});
      }),
      catchError((err: any) => throwError(err))
    );
  }

  deleteWithoutMsg(id: number): Observable<EventRef> {
    return this.api.deleteWithoutMsg(this.apiEndpoint, id).pipe(
      tap((deleted: EventRef) => {
        this.stateService.setStateOnDelete(deleted, this._data);
        this._selectedData.next({});
      }),
      catchError((err: any) => throwError(err))
    );
  }

  update(data: EventRef): Observable<EventRef> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        map((updated: EventRefBackend) => updated),
        tap((updated: EventRef) =>
          this.stateService.setStateOnUpdate(updated, this._data)
        ),
        catchError((err: any) => throwError(err))
      );
  }

  updateWithoutMsg(data: EventRef): Observable<EventRef> {
    return this.api
      .putWithoutMsg(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        map((updated: EventRefBackend) => updated),
        tap((updated: EventRef) =>
          this.stateService.setStateOnUpdate(updated, this._data)
        ),
        catchError((err: any) => throwError(err))
      );
  }

  createNewTodo(): void {
    this._selectedData.next({});
  }

  selectKategoria(selected: number[]): void {
    this._activeKategoriak.next(selected);
  }

  selectDate(event: EventRef): void {
    this._selectedData.next(event);
  }

  selectEvent(event?: EventApi): void {
    if (!event) {
      this._selectedData.next({});
      return;
    }

    let eventRef = new EventRef();
    console.log('event', event)

    eventRef.id = +event.id;
    //eventRef.title = event.title;
    eventRef.start = event.start;
    eventRef.end = event.start
    eventRef.datum = event.start;
    //eventRef.szabadsagtipus_id = event.szabadsagtipus_id
    //eventRef.allDay = event.allDay;
    eventRef = {
      ...eventRef,
      ...event.extendedProps,
    };

    this._selectedData.next(eventRef);
  }

  fetchData(): Observable<EventRef[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(this.apiEndpoint),
      this.szabiTipusokService.initData$,
      this.oekService.initData$
    ]).pipe(
      map(([events, szabiTipusok, oek]) => this.fromJSON(events, szabiTipusok, oek)),
      tap((data: EventRef[]) => {
        this._data.next(data);
        this.initialValue = data;
        localStorage.setItem('szabadsagnaptar', JSON.stringify({lastFetch: this.lastFetch}));
      }),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  fetchDataByTelephely(telephely_id: number): Observable<EventRef[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(`${this.szabadsagByTelephelyEndpoint}/${telephely_id}`),
      this.szabiTipusokService.initData$,
      this.oekService.initData$
    ]).pipe(
      map(([events, szabiTipusok, oek]) => this.fromJSON(events, szabiTipusok, oek)),
      tap((data: EventRef[]) => {
        this._data.next(data);
        this.initialValue = data;
        localStorage.setItem('szabadsagnaptar', JSON.stringify({lastFetch: this.lastFetch}));
      }),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private toJSON(data: EventRef): EventRefBackend {
    return {
      id: data.id,
      //cim: data.title,
      // esemeny_kezdete: data.start,
      // esemeny_vege: data.end,
      // egesznap: data.allDay,
      datum: data.datum,
      dolgozo_id: data.dolgozo_id || null,
      szabadsag_tipusok_id: data.szabadsag_tipusok_id || null,
      leiras: data.leiras || null,
      statusz: data.statusz || 0
    };
  }

  private fromJSON(
    data: EventRefBackend | EventRefBackend[],
    szabiTipusok: any[],
    oek: any[]
  ): any {
    // Array
    if (Array.isArray(data)) {
      return data.filter(e => findByProp(szabiTipusok, 'id', e.szabadsag_tipusok_id))
      .map((e: EventRefBackend) => ({
        id: e.id,
        title: (findByProp(szabiTipusok, 'id', e.szabadsag_tipusok_id)?.megnevezes || 'Fizetett szabadság') + ' (' + (e.statusz == 1 ? 'elfogadva)' : 'függőben)'),
        start: new Date(e.datum),
        end: new Date(e.datum),
        datum: new Date(e.datum),
        oe_id: findByProp(oek, 'id', e.dolgozo_id)?.ceg_id,
        allDay: true,
        editable: true,
        szabadsag_tipusok_id: e.szabadsag_tipusok_id,
        szabadsag_kod: findByProp(szabiTipusok, 'id', e.szabadsag_tipusok_id)?.kod,
        statusz: e.statusz || 0,
        dolgozo_id: e.dolgozo_id,
        backgroundColor: findByProp(szabiTipusok, 'id', e.szabadsag_tipusok_id)?.szin,
        textColor: this.getContrastColor(findByProp(szabiTipusok, 'id', e.szabadsag_tipusok_id)?.szin),
        leiras: e.leiras,
      }));
    }

    // Object
    else {
      if(findByProp(szabiTipusok, 'id', data.szabadsag_tipusok_id)) {
        return {
          id: data.id,
          title: findByProp(szabiTipusok, 'id', data.szabadsag_tipusok_id)?.megnevezes || 'Fizetett szabadság',
          start: new Date(data.datum),
          end: new Date(data.datum),
          allDay: true,
          editable: true,
          datum: new Date(data.datum),
          oe_id: findByProp(oek, 'id', data.dolgozo_id)?.ceg_id,
          szabadsag_tipusok_id: data.szabadsag_tipusok_id,
          szabadsag_kod: findByProp(szabiTipusok, 'id', data.szabadsag_tipusok_id)?.kod,
          dolgozo_id: data.dolgozo_id,
          leiras: data.leiras,
          backgroundColor: findByProp(szabiTipusok, 'id', data.szabadsag_tipusok_id)?.szin,
          textColor: this.getContrastColor(findByProp(szabiTipusok, 'id', data.szabadsag_tipusok_id)?.szin),
          statusz: data.statusz || 0,
        };
      }
    }
  }

  public updateCheckedState(data: any, id?: number): void {
    const state = this._data.value;

    const filteredArray = this.initialValue?.filter(
      (e: EventRef) => 
        data.find((k: any) => k.id == e.szabadsag_tipusok_id)?.checked
    );

    this._data.next(filteredArray);
  }

  public updateCheckedStateMain(data: any, id?: number): void {
    const state = this._data.value;
    const filteredArray = this.initialValue?.filter(
      (e: any) => 
        data.find((k: any) => k.kod == e.szabadsag_kod)?.checked || e.szabadsag_tipusok_id == 0
    );

    this._data.next(filteredArray);
  }

  getSzabinap(datum: Date, telephely: any): Observable<any> {
    return this.api.postWithoutMsg(`${this.szabadsagOKEndpoint}`, {datum: datum, telephely: telephely});
  }

  getNaptarChange(): any {
    this.lastFetch = new Date();
    const last = JSON.parse(localStorage.getItem('szabadsagnaptar'))?.lastFetch;

    if(!last) {
      return
    }
    this.api.get(`${this.szabadsagNaptarUpdateEndpoint}/${last}`).pipe(
        // take(1),
        //tap((data: any[]) => this._data.next(data)),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
    )
    .subscribe((valtozas: any) => {
      if(valtozas) {
        if(this._data.value) {
          if(valtozas.valtozas.length > 0) {
            valtozas.valtozas.forEach(szabinap => { 
              const biz = findByProp(this._data.value, 'id', szabinap.id);
              if(biz) {
                this.stateService.setStateOnUpdate(szabinap, this._data);
                //this.dbService.update('ajanlat', bizonylat).subscribe();
              } else {
                this.stateService.setStateOnAdd(szabinap, this._data);
                //this.dbService.add('ajanlat', bizonylat).subscribe();
              }
            });
          }

          if(valtozas.torolt_lista.length > 0) { 
            valtozas.torolt_lista.forEach(szabinap => {
              const biz = findByProp(this._data.value, 'id', szabinap.modul_id);
                if(biz) {
                  this.stateService.setStateOnDelete(biz ,this._data);
                  //this.dbService.deleteByKey('ajanlat', bizonylat.modul_id).subscribe();
                }
            })
          }

          localStorage.setItem('szabadsagnaptar', JSON.stringify({lastFetch: this.lastFetch}));
        }
      }
    }
    );
  }

  getTelephelyChange(telephely_id: any): any {
    this.api.get(`${this.szabadsagByTelephelyEndpoint}/${telephely_id}`).pipe(
        // take(1),
        //tap((data: any[]) => this._data.next(data)),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
    )
    .subscribe((valtozas: any) => {
      if(valtozas) {
        if(this._data.value) {
          if(valtozas.valtozas.length > 0) {
            valtozas.valtozas.forEach(szabinap => { 
              const biz = findByProp(this._data.value, 'id', szabinap.id);
              if(biz) {
                this.stateService.setStateOnUpdate(szabinap, this._data);
                //this.dbService.update('ajanlat', bizonylat).subscribe();
              } else {
                this.stateService.setStateOnAdd(szabinap, this._data);
                //this.dbService.add('ajanlat', bizonylat).subscribe();
              }
            });
          }

          if(valtozas.torolt_lista.length > 0) { 
            valtozas.torolt_lista.forEach(szabinap => {
              const biz = findByProp(this._data.value, 'id', szabinap.modul_id);
                if(biz) {
                  this.stateService.setStateOnDelete(biz ,this._data);
                  //this.dbService.deleteByKey('ajanlat', bizonylat.modul_id).subscribe();
                }
            })
          }

          localStorage.setItem('szabadsagnaptar', JSON.stringify({lastFetch: this.lastFetch}));
        }
      }
    }
    );
  }

  getSzabinapById(dolgozo_id: any): Observable<any> {
    return this.api.get(`${this.dolgozoSzabiEndpoint}/${dolgozo_id}`);
  }

  getContrastColor(rgb: string): string {
    // Extract the RGB values from the input string
    const [r, g, b] = rgb.match(/\d+/g)?.map(Number) || [0, 0, 0];

    // Calculate luminance using the given formula
    const luminance = (r * 0.299) + (g * 0.587) + (b * 0.114);
    // Return either #000000 or #ffffff based on the luminance
    return luminance > 186 ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)';
}
}
